// Apps → ASO Intelligence client: saved market samples (scopes), their runs
// and the competitor listings a run collected (see api/cloudflare/src/lib/aso).
import { api } from './client';

const BASE = '/admin/apps-setup/aso';

export type ScopeKind = 'search' | 'category';
export type ScopeLocales = 'geo' | 'all';
export type ScopeSchedule = 'manual' | 'daily' | 'weekly';
export type RunStatus = 'queued' | 'running' | 'full' | 'partial' | 'failed';

export interface RunSummary {
  id: number; status: RunStatus; started_at: number; finished_at: number | null;
  apps_total: number; apps_ok: number;
}

export interface AsoScope {
  id: number; name: string; store: string; geo: string; kind: ScopeKind; query: string;
  top_n: number; locales: ScopeLocales; schedule: ScopeSchedule; enabled: number; keywords_enabled: number;
  last_run_at: number | null; created_by: string | null; created_by_name: string | null;
  created_at: number; updated_at: number;
  locale_list: string[];
  run_count: number;
  last_run: RunSummary | null;
}

export interface ScopeInput {
  name: string; geo: string; kind: ScopeKind; query: string; top_n: number;
  locales: ScopeLocales; schedule: ScopeSchedule; enabled: boolean; keywords_enabled: boolean;
}

export interface AsoRun extends RunSummary {
  scope_id: number; error: string | null; tasks_pending: number; tasks_total: number;
  analysis: AnalysisStatus; analyzed_at: number | null; analysis_error: string | null;
  keywords: KeywordsStatus; keywords_at: number | null; keywords_error: string | null;
}

export interface RunApp {
  app_id: number; rank: number; status: 'pending' | 'ok' | 'failed'; error: string | null; role: 'sample' | 'ours';
  store_id: string; name: string | null; developer: string | null; category: string | null;
  rating: number | null; installs: string | null; icon_hash: string | null; is_ours: number;
  locales_ok: number; locales_total: number; title: string | null; short_desc: string | null; shots: number;
}

export interface RunDetail {
  run: AsoRun;
  scope: AsoScope | null;
  primary_locale: string;
  apps: RunApp[];
}

export interface AppLocale {
  id: number; locale: string; country: string; available: number;
  title: string | null; short_desc: string | null; full_desc: string | null;
  icon_hash: string | null; feature_hash: string | null; video_url: string | null;
  screenshots: { pos: number; hash: string }[];
  error: string | null; fetched_at: number;
}

export interface RunAppDetail {
  app: {
    id: number; store_id: string; name: string | null; developer: string | null; category: string | null;
    rating: number | null; installs: string | null; icon_hash: string | null; is_ours: number;
    rank: number; sample_status: string;
  };
  locales: AppLocale[];
}

export const listScopes = () => api.get<AsoScope[]>(`${BASE}/scopes`);
export const createScope = (body: ScopeInput) => api.post<AsoScope>(`${BASE}/scopes`, body);
export const updateScope = (id: number, body: Partial<ScopeInput>) => api.put<AsoScope>(`${BASE}/scopes/${id}`, body);
export const deleteScope = (id: number) => api.delete<null>(`${BASE}/scopes/${id}`);
export const runScope = (id: number) => api.post<{ run_id: number }>(`${BASE}/scopes/${id}/run`, {});
export const listRuns = (scopeId: number) => api.get<AsoRun[]>(`${BASE}/scopes/${scopeId}/runs`);
export const getRun = (runId: number) => api.get<RunDetail>(`${BASE}/runs/${runId}`);
export const getRunApp = (runId: number, appId: number) => api.get<RunAppDetail>(`${BASE}/runs/${runId}/apps/${appId}`);
export const geoLocales = (geo: string) => api.get<string[]>(`${BASE}/geo-locales/${encodeURIComponent(geo)}`);
export const getAsset = (hash: string) => api.blob(`${BASE}/assets/${hash}`);

// --- phase 2: patterns, taxonomy, settings ---------------------------------

export type AnalysisStatus = 'none' | 'queued' | 'done' | 'failed';
export type TaxonomyKind = 'tag' | 'hook' | 'cluster';
export type TaxonomyStatus = 'active' | 'pending' | 'rejected';

export interface TaxonomyEntry {
  id: number; kind: TaxonomyKind; slug: string; label: string; description: string | null;
  status: TaxonomyStatus; created_by: string; created_at: number;
}

export interface TextPattern {
  id: number; locale: string; phrase: string; ngram: number; cluster_slug: string | null;
  placement: 'title' | 'short' | 'full'; apps_count: number; apps_total: number;
  examples: { app_id: number; name: string; snippet: string }[];
}
export interface VisualPattern {
  id: number; kind: 'tag' | 'hook'; slug: string; asset_kind: 'icon' | 'screenshot' | 'feature'; position: number | null;
  apps_count: number; apps_total: number; examples: { app_id: number; hash: string }[];
}
export interface SequencePattern {
  id: number; sequence: string[]; apps_count: number; apps_total: number;
  examples: { app_id: number; hashes: string[] }[];
}
export interface RunPatterns {
  run: { id: number; status: RunStatus; analysis: AnalysisStatus; analyzed_at: number | null; analysis_error: string | null; pending: Record<string, number> };
  labels: Record<TaxonomyKind, Record<string, string>>;
  app_names: Record<number, string>;
  text: TextPattern[];
  visual: VisualPattern[];
  sequences: SequencePattern[];
}
export interface AssetAnalysis {
  hash: string; model: string; analyzed_at: number; summary: string | null;
  tags: string[]; primary_hook: string | null; secondary_hooks: string[]; raw: Record<string, unknown>;
}
export interface AsoSettings {
  vision_model: string; text_model: string; vision_budget: number; auto_analyze: boolean;
  keyword_provider: string; keyword_budget: number; keyword_providers?: string[];
  asomobile_token_set: boolean; asomobile_token_masked: string;
  /** Write-only: set to store a new token, empty string to clear. */
  asomobile_token?: string;
  defaults?: { vision_model: string; text_model: string; vision_budget: number; keyword_budget: number };
}

export const getRunPatterns = (runId: number) => api.get<RunPatterns>(`${BASE}/runs/${runId}/patterns`);
export const analyzeRun = (runId: number, force = false) => api.post<{ vision: number; skipped: number }>(`${BASE}/runs/${runId}/analyze`, { force });
export const getAssetAnalysis = (hash: string) => api.get<AssetAnalysis>(`${BASE}/assets/${hash}/analysis`);
export const listTaxonomy = () => api.get<TaxonomyEntry[]>(`${BASE}/taxonomy`);
export const createTaxonomy = (body: { kind: TaxonomyKind; slug?: string; label: string; description?: string }) => api.post<TaxonomyEntry>(`${BASE}/taxonomy`, body);
export const updateTaxonomy = (id: number, body: Partial<Pick<TaxonomyEntry, 'label' | 'description' | 'status'>>) => api.put<TaxonomyEntry>(`${BASE}/taxonomy/${id}`, body);
export const deleteTaxonomy = (id: number) => api.delete<null>(`${BASE}/taxonomy/${id}`);
export const getAsoSettings = () => api.get<AsoSettings>(`${BASE}/settings`);
export const putAsoSettings = (body: Partial<AsoSettings>) => api.put<AsoSettings>(`${BASE}/settings`, body);

// --- phase 3: keywords, compare, brief pack --------------------------------

export type KeywordsStatus = 'none' | 'queued' | 'done' | 'failed';

export interface RunKeyword {
  keyword_id: number; keyword: string; cluster_slug: string | null; provider: string;
  apps_count: number; best_rank: number | null; traffic: number | null; difficulty: number | null; kei: number | null;
  previous: { traffic: number | null; apps_count: number } | null;
  apps: { app_id: number; name: string; rank: number | null }[];
}
export interface RunKeywords {
  run: { id: number; keywords: KeywordsStatus; keywords_at: number | null; keywords_error: string | null; pending: number; previous_run_id: number | null };
  labels: Record<TaxonomyKind, Record<string, string>>;
  keywords: RunKeyword[];
}
export interface CompareCandidate { id: string; name: string; bundle_id: string; icon_url: string | null; aso_app_id: number | null }
export interface KeywordGap {
  keyword: string; traffic: number | null; difficulty: number | null; kei: number | null; cluster_slug: string | null;
  competitors_ranking: number; best_rank: number | null; competitors_in_title: number;
  our_rank: number | null; in_title: boolean; in_short: boolean; in_full: boolean;
}
export interface Gaps {
  our_app: { id: number; collected: boolean };
  apps_total: number;
  pending: number;
  labels: Record<TaxonomyKind, Record<string, string>>;
  keywords: KeywordGap[];
  phrases: { phrase: string; placement: string; apps_count: number; apps_total: number; cluster_slug: string | null; we_use: boolean }[];
  hooks: { slug: string; apps_count: number; apps_total: number; we_use: boolean }[];
  icon_tags: { slug: string; apps_count: number; apps_total: number; we_use: boolean }[];
  our_hooks: string[];
  our_icon_tags: string[];
}
export interface BriefPack {
  run: { id: number; analysis: AnalysisStatus; keywords: KeywordsStatus };
  keywords: { keyword: string; traffic: number | null; apps_count: number }[];
  title_phrases: { phrase: string; apps_count: number; apps_total: number }[];
  short_phrases: { phrase: string; apps_count: number; apps_total: number }[];
  sequence: { hooks: string[]; apps_count: number; apps_total: number } | null;
  screenshot_hooks: { slug: string; apps_count: number; apps_total: number }[];
  icon_tags: { slug: string; apps_count: number; apps_total: number }[];
  labels: Record<string, Record<string, string>>;
}

export const getRunKeywords = (runId: number) => api.get<RunKeywords>(`${BASE}/runs/${runId}/keywords`);
export const collectRunKeywords = (runId: number) => api.post<{ queued: number }>(`${BASE}/runs/${runId}/keywords`, {});
export const compareCandidates = (runId: number) => api.get<CompareCandidate[]>(`${BASE}/runs/${runId}/compare/candidates`);
export const addCompareApp = (runId: number, pkg: string) => api.post<{ app_id: number; already: boolean; role: string }>(`${BASE}/runs/${runId}/compare`, { package: pkg });
export const getGaps = (runId: number, appId: number) => api.get<Gaps>(`${BASE}/runs/${runId}/gaps?app=${appId}`);
export const getBriefPack = (runId: number) => api.get<BriefPack>(`${BASE}/runs/${runId}/brief-pack`);
